import React, { useEffect, useId } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/Dialog';
import { Box, Button, FormControl, FormHelperText } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Input from 'components/Input';
import { useContentLibrary } from 'features/content-library/useContentLibrary';
import { selectFile } from 'features/content-library/contentLibrarySlice';
import { renameContentLibraryFileRequested } from 'features/content-library/actions';
import { FileType, RenameContentLibraryItemPayload } from 'features/content-library/types';

export interface RenameContentLibraryFileValues {
  fileName: string;
}

const RenameContentLibraryFileModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const fileId = modalData.fileId as string;
  const type = modalData.type as FileType;

  const file = useAppSelector((state) => selectFile(state, fileId));

  const { isFeatureAvailable } = useContentLibrary();

  const {
    handleSubmit,
    formState: { errors, isValid },
    control,
    setFocus,
  } = useForm<RenameContentLibraryFileValues>({
    mode: 'onChange',
    defaultValues: {
      fileName: file?.name || '',
    },
  });

  const shouldClose = !isFeatureAvailable || !file;

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal('renameContentLibraryFile'));
    }
  }, [shouldClose, dispatch]);

  useEffect(() => {
    setTimeout(() => {
      setFocus('fileName');
    }, 0);
  }, [setFocus]);

  const handleCancel = () => {
    dispatch(closeModal('renameContentLibraryFile'));
  };

  const handleOk: SubmitHandler<RenameContentLibraryFileValues> = (data) => {
    if (file) {
      const payload: RenameContentLibraryItemPayload = {
        id: fileId,
        name: data.fileName.trim(),
        personal: file.personal,
      };

      dispatch(renameContentLibraryFileRequested(payload));
      dispatch(closeModal('renameContentLibraryFile'));
    }
  };

  return (
    <Dialog
      open={!shouldClose}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      onClose={handleCancel}
    >
      <DialogTitle id={`${modalId}-title`}>
        {type === 'whiteboard'
          ? t('content_library.rename_file.whiteboard_title')
          : t('content_library.rename_file.title')}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1,
          }}
        >
          <FormControl fullWidth error={!!errors.fileName}>
            <Controller
              name="fileName"
              control={control}
              rules={{
                required: t('common:validation.field_required'),
                validate: (value) => !!value.trim() || t('common:validation.field_required'),
              }}
              render={({ field: { ref, ...field } }) => (
                <Input
                  {...field}
                  inputProps={{
                    'aria-label': t('content_library.rename_file.input_name_placeholder'),
                  }}
                  placeholder={t('content_library.rename_file.input_name_placeholder')}
                  autoComplete="off"
                  inputRef={ref}
                />
              )}
            />
            <FormHelperText>{errors.fileName?.message}</FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="grayscale" onClick={handleCancel}>
          {t('content_library.rename_file.btn_cancel_label')}
        </Button>
        <Button type="button" onClick={handleSubmit(handleOk)} disabled={!isValid}>
          {t('content_library.rename_file.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameContentLibraryFileModal;
